import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, map } from 'rxjs';

import { ViewportMode } from '@blaze-shared/elements-domain';

import { Calendar } from '../calendar.models';

@UntilDestroy()
@Component({
  selector: 'blaze-calendars-filter',
  templateUrl: './calendars-filter.component.html',
  styleUrls: ['./calendars-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalendarsFilterComponent implements OnInit, OnChanges {
  calendarsSelectionChange = output<{ ids: string[]; all: boolean }>();

  @Input() calendars: Calendar[] = [];
  @Input() calendarsStyles: Record<string, Record<string, string>> = {};
  @Input() viewportMode: ViewportMode | undefined;

  private selectedCalendarsIds: string[] = [];

  form: FormGroup = new FormGroup({
    filter: new FormArray([]),
  });
  allSelected = true;

  @HostBinding('class.mobile') get isMobile(): boolean {
    return this.viewportMode === 'mobile';
  }

  get filtersFormGroups(): FormGroup[] {
    return (this.form.get('filter') as FormArray).controls as FormGroup[];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['calendars']) {
      this.form.setControl(
        'filter',
        new FormArray(
          this.calendars
            .filter((calendar) => Boolean(calendar.id))
            .map(
              (calendar) =>
                new FormGroup({
                  id: new FormControl(calendar.id),
                  name: new FormControl(calendar.name),
                  selected: new FormControl(
                    !this.selectedCalendarsIds.length ||
                      this.allSelected ||
                      (calendar.id && this.selectedCalendarsIds.includes(calendar.id)),
                  ),
                }),
            ),
        ),
      );
    }
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        map((value: { filter: { id: string; name: string; selected: boolean }[] }) =>
          value.filter.filter((calendar) => calendar.selected).map((calendar) => calendar.id),
        ),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((selectedCalendarsIds) => {
        this.selectedCalendarsIds = selectedCalendarsIds;
        this.allSelected = selectedCalendarsIds.length === this.filtersFormGroups.length;
        this.calendarsSelectionChange.emit({ ids: selectedCalendarsIds, all: this.allSelected });
      });
  }

  someSelected(): boolean {
    return (
      this.filtersFormGroups.filter((fg) => fg.controls['selected'].value).length > 0 &&
      !this.allSelected
    );
  }

  toggleAll(selected: boolean): void {
    const filters = this.form.value['filter'];
    filters.forEach((filter: { id: string; name: string; selected: boolean }) => {
      filter.selected = selected;
    });

    this.form.controls['filter'].setValue(filters);
  }

  updateSelectedCalendarsIds(ids: string[]): void {
    this.selectedCalendarsIds = ids;
  }
}
